import { UrlEventType, UrlParameter, VisitType } from 'src/requester';
import { CustomDataType, IKameleoonData, KameleoonData } from './types';
import { TrackingStatus } from 'src/types';

/**
 * @class
 * CustomData - a class for creating an instance for user's custom data
 * */
export class CustomData implements IKameleoonData {
  static readonly UNDEFINED_INDEX = -1;

  private _status: TrackingStatus;
  private _index: number;
  private value: string[];
  private isIdentifier: boolean;
  private overwrite: boolean;
  private _name?: string;

  /**
   * @param {number|string} indexOrName - either:
   *   - `number` — an index of custom data (configured in *Advanced Tools* section of Kameleoon Application)
   *   - `string` — a custom name for the data
   * @param {boolean} [overwrite=true] - optional flag, whether to overwrite existing data
   * @param {...string} value - one or more values to store. Values must be strings (or stringified).
   *
   * @example
   * ```ts
   * // - Using index with single value
   * const cd1 = new CustomData(0, 'value_1');
   *
   * // - Using index with multiple values
   * const cd2 = new CustomData(0, 'value_1', 'value_2');
   *
   * // - Using index with overwrite flag
   * const cd3 = new CustomData(0, false, 'value_1', 'value_2');
   *
   * // - Using name with single value
   * const cd4 = new CustomData('name', 'value_1');
   *
   * // - Using name with multiple values
   * const cd5 = new CustomData('name', 'value_1', 'value_2');
   *
   * // - Using name with overwrite flag
   * const cd6 = new CustomData('name', false, 'value_1', 'value_2');
   * ```
   */
  constructor(index: number, ...value: string[]);
  constructor(index: number, overwrite: boolean, ...value: string[]);
  constructor(name: string, ...value: string[]);
  constructor(name: string, overwrite: boolean, ...value: string[]);
  constructor(
    first: number | string,
    second?: boolean | string,
    ...rest: string[]
  ) {
    this._status = TrackingStatus.Unsent;
    this.isIdentifier = false;

    const isNumber = typeof first === 'number';
    const isBoolean = typeof second === 'boolean';

    if (isNumber) {
      this._index = first as number;
    } else {
      this._name = first as string;
      this._index = CustomData.UNDEFINED_INDEX;
    }

    this.overwrite = isBoolean ? (second as boolean) : true;
    this.value = isBoolean
      ? rest
      : [second as string, ...rest].filter((v) => v != null);
  }

  get url(): string {
    // --- Note ---
    // If SDK is used in vanilla JS codebase, then you're also able to create an instance
    // with no required data, we don't want send anything to tracking in that case
    if (typeof this._index !== 'number') {
      return '';
    }

    const uniqueValues = [...new Set(this.value)];
    let resultValue: Record<string, 1> = {};

    // --- Note ---
    // Final value should look like this `{ "value_1": 1, "value_2": 1, "value_3": 1 }`
    // With no duplicates
    uniqueValues.forEach((value) => {
      resultValue[value] = 1;
    });

    let identifierParameter = '';

    if (this.isIdentifier) {
      identifierParameter = UrlParameter.MappingIdentifier + String(true);
    }

    return (
      UrlEventType.CustomData +
      UrlParameter.Index +
      this._index +
      UrlParameter.ValuesCountMap +
      encodeURIComponent(JSON.stringify(resultValue)) +
      UrlParameter.Overwrite +
      String(this.overwrite) +
      identifierParameter
    );
  }

  get data(): CustomDataType {
    return {
      index: this._index,
      ...(this._name !== undefined ? { name: this._name } : {}),
      value: this.value,
      type: KameleoonData.CustomData,
      isIdentifier: this.isIdentifier,
      status: this.status,
      overwrite: this.overwrite,
    };
  }

  get status(): TrackingStatus {
    if (this._isMappingIdentifier) {
      return TrackingStatus.Unsent;
    }

    return this._status;
  }

  /**
   * @private
   * @method _fromRaw - an internal method for creating a CustomData instance from raw data
   * @param {CustomDataType} data - a raw data
   * @return {CustomData} a CustomData instance
   * */
  public static _fromRaw(data: CustomDataType): CustomData {
    const { index, value, status, isIdentifier, name, overwrite } = data;
    let customData;
    if (name) {
      customData = new CustomData(name, overwrite ?? true, ...value);
      customData.index = index;
    } else {
      customData = new CustomData(index, overwrite ?? true, ...value);
    }

    customData.status = status;
    customData._isMappingIdentifier = isIdentifier;

    return customData;
  }

  /**
   * @property isMappingIdentifier - signifying if the data is a mapping identifier
   * @private
   * */
  set _isMappingIdentifier(value: boolean) {
    this.isIdentifier = value;
  }

  set status(status: TrackingStatus) {
    this._status = status;
  }

  /**
   * @private
   * @method _updateFromVisit - an internal method for updating a list of custom data from a visit
   * @param {VisitType} visit - a visit
   * */
  public static _updateFromVisit(
    visit: VisitType,
    dataMap: Map<number, CustomData>,
  ): void {
    if (visit.customDataEvents?.length) {
      for (let i = visit.customDataEvents.length - 1; i >= 0; i--) {
        const { index, valuesCountMap } = visit.customDataEvents[i].data;

        if (!dataMap.has(index)) {
          dataMap.set(
            index,
            new CustomData(index, ...Object.keys(valuesCountMap)),
          );
        }
      }
    }
  }

  /**
   * @private
   * @method index - an internal setter for setting index of custom data
   * @param {number} value - an index value
   * */
  public set index(value: number) {
    this._index = value;
  }

  /**
   * @private
   * @method index - an internal getter for index of custom data
   * */
  public get index(): number {
    return this._index;
  }

  /**
   * @private
   * @method name - an internal getter for a name of custom data
   * */
  public get name(): string | undefined {
    return this._name;
  }

  /**
   * @private
   * @method name - an internal getter for a name of custom data
   * */
  public get values(): string[] {
    return this.value;
  }
}
